import React from 'react';
import { useSelector } from 'react-redux';

import { IColumn } from '@xcritical/grid';

import { DashboardWithStore } from '@crm-framework/dashboard';

import { renderCurrencyColumn } from '@crm-package/utils';
import { getPageSizeOptions } from '@crm-package/app';

import {
  apiExcelExport,
  apiGetDashboard,
  apiGetGridData,
  apiGetGridAllData,
  defaultApi,
} from './api';
import { IDashBoardWithStoreProps } from './types';
import { filterFormatters } from './filter-formatters';
import {
  DemoToggler,
  TimeZonePopover,
  DashboardModeCheckbox,
  ExportWithEmailButton,
} from './components';
import { customFilters, DEFAULT_GRID_ROWS_NUMBER } from './consts';

const gridColumnsFormatterByType = {
  Currency: (column: IColumn): IColumn => ({
    ...column,
    render: renderCurrencyColumn,
  }),
};

export const Dashboard: React.VFC<IDashBoardWithStoreProps> = ({
  gridColumnApiUrl,
  gridDataApiUrl,
  excelApiUrl,
  totalsApiUrl,
  customApi,
  defaultColumns,
  defaultFilters,
  disableFastMode,
  filterActions,
  gridActions,
  withEmailExport,
  isSupportCryptoCurrencies,
  ...dashboardProps
}) => {
  const pageSizeOptions = useSelector(getPageSizeOptions);
  const pageSizeOptionsWithDefault = Array.from(
    new Set([...pageSizeOptions, ...DEFAULT_GRID_ROWS_NUMBER])
  ).sort((prev, next) => prev - next);

  const api = {
    ...defaultApi,
    apiGetDashboard: () =>
      apiGetDashboard(
        gridColumnApiUrl!,
        disableFastMode,
        isSupportCryptoCurrencies,
        dashboardProps.dashboardName
      ),
    apiExcelExport: async (data, payload) =>
      apiExcelExport(excelApiUrl!, data, payload),
    apiGetGridData: (data) =>
      apiGetGridData({
        ...data,
        gridApi: gridDataApiUrl!,
        totalsApi: totalsApiUrl,
        dashboardName: dashboardProps.dashboardName,
      }),
    apiGetGridAllData: (data, totalCount) =>
      apiGetGridAllData(
        {
          ...data,
          gridApi: gridDataApiUrl!,
        },
        totalCount
      ),
    ...customApi,
  };

  return (
    <DashboardWithStore
      api={api}
      defaultColumns={defaultColumns}
      defaultFilters={defaultFilters}
      excelApiUrl={excelApiUrl}
      gridColumnsFormatterByType={gridColumnsFormatterByType}
      filterFormatter={filterFormatters}
      pageSizeOptions={pageSizeOptionsWithDefault}
      overrideFilters={customFilters}
      filterActions={
        <>
          <DemoToggler dashboardName={dashboardProps.dashboardName} />
          <TimeZonePopover dashboardName={dashboardProps.dashboardName} />
          <DashboardModeCheckbox dashboardName={dashboardProps.dashboardName} />
          {filterActions}
        </>
      }
      gridActions={
        <>
          {gridActions}{' '}
          {withEmailExport && (
            <ExportWithEmailButton
              dashboardName={dashboardProps.dashboardName}
            />
          )}
        </>
      }
      {...dashboardProps}
    />
  );
};
